import { apiClient } from '@shared/utils/api-client';
import axiosInstance from '@shared/utils/axios-instance';

export type SellerFinanceIntegrationKind = 'MOCK' | 'TBANK_BULK_OUTBOX';

export interface SellerFinanceConfigDto {
    payoutWeekdayUtc: number;
    payoutHourUtc: number;
    payoutMinuteUtc: number;
    integrationKind: SellerFinanceIntegrationKind;
    nextScheduledPayoutAt: string;
    tbankOpenApiConfigured: boolean;
    updatedAt: string;
}

export interface SellerFinanceBatchSummary {
    id: number;
    accrualPeriodStart: string;
    accrualPeriodEndExclusive: string;
    scheduledExecuteAt: string | null;
    executedAt: string | null;
    status: string;
    failureMessage: string | null;
    memo: string | null;
    payoutsCount: number;
    netTotal: number;
    createdAt: string;
}

export interface SellerFinancePayoutLineDto {
    id: number;
    batchId: number | null;
    sellerId: number;
    shopName: string;
    slug: string;
    accrualPeriodStart: string | null;
    accrualPeriodEndExclusive: string | null;
    ordersCount: number;
    itemsSold: number;
    grossRevenue: number;
    commissionAmount: number;
    promotionDeduction: number;
    deliveryDeduction: number;
    otherDeductions: unknown;
    netAmount: number;
    status: string;
    paid: boolean;
    paidAt: string | null;
    externalPaymentId: string | null;
    comment: string | null;
    tbankReceiptUrl?: string | null;
    updDocument?: {
        id: number;
        documentNumber: string;
        pdfPublicUrl: string | null;
        invoicePdfPublicUrl?: string | null;
        sendStatus?: string;
    } | null;
}

export interface SellerFinanceBatchDetail extends Omit<
    SellerFinanceBatchSummary,
    'netTotal' | 'payoutsCount'
> {
    payouts: Array<{
        id: number;
        sellerId: number;
        shopName: string;
        slug: string;
        ordersCount: number;
        itemsSold: number;
        grossRevenue: number;
        commissionAmount: number;
        promotionDeduction: number;
        deliveryDeduction: number;
        otherDeductions: unknown;
        netAmount: number;
        status: string;
        paidAt: string | null;
        externalPaymentId: string | null;
        comment: string | null;
        tbankReceiptUrl?: string | null;
    }>;
}

export interface UpdateSellerFinanceConfigPayload {
    payoutWeekdayUtc: number;
    payoutHourUtc: number;
    payoutMinuteUtc: number;
    integrationKind: SellerFinanceIntegrationKind;
}

export interface SellerFinanceOpenWeekPreviewRow {
    sellerId: number;
    shopName: string;
    slug: string;
    ordersCount: number;
    itemsSold: number;
    grossRevenue: number;
    commissionAmount: number;
    /** PROMOTION за период (ledger). */
    promotionDeduction: number;
    /** Реклама за период (ledger ADVERTISING_DEDUCTION). */
    advertisingDeduction: number;
    /** Штрафы за отмену за период (ledger). */
    sellerCancelPenalty: number;
    /** Удержание по доставке за период (по сумме DELIVERY_ADJUSTMENT; для столбца — негативный вклад). */
    deliveryDeduction: number;
    netToSeller: number;
}

export interface SellerFinanceOpenWeekPreviewDto {
    accrualPeriodStart: string;
    accrualPeriodEndExclusive: string;
    salesDateFrom: string;
    salesDateTo: string;
    weekSundayDate: string;
    batchForPeriodId: number | null;
    commissionPercent: number | null;
    rows: SellerFinanceOpenWeekPreviewRow[];
    totals: {
        ordersCount: number;
        itemsSold: number;
        grossRevenue: number;
        commissionAmount: number;
        promotionDeduction: number;
        advertisingDeduction: number;
        sellerCancelPenalty: number;
        deliveryDeduction: number;
        netToSeller: number;
    };
}

export interface SellerFinanceSettledSellerTotalsRow {
    sellerId: number;
    shopName: string;
    slug: string;
    payoutLinesCount: number;
    ordersCount: number;
    itemsSold: number;
    grossRevenue: number;
    commissionAmount: number;
    promotionDeduction: number;
    advertisingDeduction: number;
    sellerCancelPenalty: number;
    deliveryDeduction: number;
    netToSeller: number;
}

export interface SellerFinanceSettledTotalsBySellerDto {
    rows: SellerFinanceSettledSellerTotalsRow[];
    grandTotals: Omit<SellerFinanceSettledSellerTotalsRow, 'sellerId' | 'shopName' | 'slug'>;
}

export type SellerLedgerEntryKind =
    | 'ORDER_ITEMS_REVENUE'
    | 'PICKUP_ON_RECEIPT_REVENUE'
    | 'PLATFORM_COMMISSION'
    | 'PROMOTION_DEDUCTION'
    | 'DELIVERY_ADJUSTMENT'
    | 'ADVERTISING_DEDUCTION'
    | 'SELLER_ORDER_CANCELLATION_PENALTY'
    | 'WEEKLY_PAYOUT_TRANSFER'
    | 'ORDER_ACCRUAL_STORNO'
    | 'MANUAL_ADJUSTMENT'
    | 'ADVERTISING_WALLET_TRANSFER';

export interface SellerLedgerEntryRow {
    id: number;
    kind: SellerLedgerEntryKind;
    amountRub: number;
    title: string | null;
    orderId: number | null;
    orderNumber: string | null;
    sellerPayoutId: number | null;
    occurredAt: string;
    meta: unknown;
}

export interface SellerOpenWeekAccruals {
    accrualPeriodStart: string;
    accrualPeriodEndExclusive: string;
    salesDateFrom: string;
    salesDateTo: string;
    weekSundayDate: string;
    grossRevenue: number;
    commissionAmount: number;
    promotionDeduction: number;
    advertisingDeduction: number;
    promotionAndAdvertisingRub: number;
    sellerCancelPenalty: number;
    deliveryDeduction: number;
    netAccrualRub: number;
    nextScheduledPayoutAt: string;
}

export interface SellerPlannedPayout {
    scheduledAt: string;
    salesDateFrom: string;
    salesDateTo: string;
    netAmountRub: number;
    alreadyPaid: boolean;
}

export interface SellerLedgerResponse {
    currentBalanceRub: number;
    lifetimeEarnedRub: number;
    entries: SellerLedgerEntryRow[];
    openWeekAccruals: SellerOpenWeekAccruals;
    plannedPayout: SellerPlannedPayout;
}

export const sellerFinanceApi = {
    getConfig: () => apiClient.get<SellerFinanceConfigDto>('/seller-finance/config'),

    patchConfig: (body: UpdateSellerFinanceConfigPayload) =>
        apiClient.patch<SellerFinanceConfigDto>('/seller-finance/config', body),

    listPayoutLines: (limit = 200) =>
        apiClient.get<SellerFinancePayoutLineDto[]>(
            `/seller-finance/payout-lines${limit !== 200 ? `?limit=${limit}` : ''}`,
        ),

    listSellerPayouts: (sellerId: number) =>
        apiClient.get<SellerFinancePayoutLineDto[]>(
            `/seller-finance/sellers/${sellerId}/payouts`,
        ),

    getSellerLedger: (sellerId: number) =>
        apiClient.get<SellerLedgerResponse>(
            `/seller-finance/sellers/${sellerId}/ledger`,
        ),

    getSellerAdvertising: (sellerId: number) =>
        apiClient.get<SellerAdvertisingCabinet>(
            `/seller-finance/sellers/${sellerId}/advertising`,
        ),

    payAdvertisingInvoice: (id: number) =>
        apiClient.post<SellerAdvertisingCabinet>(
            `/seller-finance/advertising-invoices/${id}/pay`,
        ),

    cancelAdvertisingInvoice: (id: number) =>
        apiClient.post(`/seller-finance/advertising-invoices/${id}/cancel`),

    payAdvertisingRefund: (id: number) =>
        apiClient.post(`/seller-finance/advertising-refunds/${id}/pay`),

    cancelAdvertisingRefund: (id: number) =>
        apiClient.post(`/seller-finance/advertising-refunds/${id}/cancel`),

    downloadUpdPdfBlob: async (
        documentId: number,
        kind: 'act' | 'invoice' = 'act',
    ): Promise<Blob> => {
        const response = await axiosInstance.get(
            `/seller-finance/upd-documents/${documentId}/pdf`,
            {
                params: { kind },
                responseType: 'blob',
                maxRedirects: 5,
            },
        );
        return response.data as Blob;
    },

    /** Текущая календарная неделя UTC (пн — сегодня): те же правила, что отчёт продаж; проверка пачки за период. */
    getOpenWeekPreview: () =>
        apiClient.get<SellerFinanceOpenWeekPreviewDto>('/seller-finance/current-week-preview'),

    getSettledPayoutTotalsBySeller: (params?: {
        periodFrom?: string;
        periodTo?: string;
    }) => {
        const q = new URLSearchParams();
        if (params?.periodFrom) q.set('periodFrom', params.periodFrom);
        if (params?.periodTo) q.set('periodTo', params.periodTo);
        const qs = q.toString();
        return apiClient.get<SellerFinanceSettledTotalsBySellerDto>(
            `/seller-finance/settled-payout-totals-by-seller${qs ? `?${qs}` : ''}`,
        );
    },

    listPayoutBatches: () =>
        apiClient.get<SellerFinanceBatchSummary[]>('/seller-finance/payout-batches'),

    getPayoutBatch: (batchId: number) =>
        apiClient.get<SellerFinanceBatchDetail>(`/seller-finance/payout-batches/${batchId}`),

    mockPreviousWeek: () =>
        apiClient.post<SellerFinanceBatchDetail>('/seller-finance/payout-batches/mock-previous-week'),

    mockCurrentWeek: () =>
        apiClient.post<SellerFinanceBatchDetail>('/seller-finance/payout-batches/mock-current-week'),

    retryPayout: (payoutId: number) =>
        apiClient.post(`/seller-finance/payouts/${payoutId}/retry`),

    syncTbankPayouts: () =>
        apiClient.post<{
            submitted: number;
            failed: number;
            completed: number;
        }>('/seller-finance/tbank/sync'),

    /** Удалить все выплаты и пачки (для локального теста повторного мока недели). */
    clearAllPayoutsForTest: () =>
        apiClient.post<{ deletedPayouts: number; deletedBatches: number }>(
            '/seller-finance/test-utils/clear-all-payouts',
        ),

    getPlatformPnl: (params?: { periodFrom?: string; periodTo?: string }) => {
        const q = new URLSearchParams();
        if (params?.periodFrom) q.set('periodFrom', params.periodFrom);
        if (params?.periodTo) q.set('periodTo', params.periodTo);
        const qs = q.toString();
        return apiClient.get<PlatformPnlDto>(
            `/seller-finance/platform-pnl${qs ? `?${qs}` : ''}`,
        );
    },

    getPlatformLedger: (params?: {
        periodFrom?: string;
        periodTo?: string;
        limit?: number;
    }) => {
        const q = new URLSearchParams();
        if (params?.periodFrom) q.set('periodFrom', params.periodFrom);
        if (params?.periodTo) q.set('periodTo', params.periodTo);
        if (params?.limit != null) q.set('limit', String(params.limit));
        const qs = q.toString();
        return apiClient.get<PlatformLedgerEntryRow[]>(
            `/seller-finance/platform-ledger${qs ? `?${qs}` : ''}`,
        );
    },

    getPlatformAdvertising: (params?: { periodFrom?: string; periodTo?: string }) => {
        const q = new URLSearchParams();
        if (params?.periodFrom) q.set('periodFrom', params.periodFrom);
        if (params?.periodTo) q.set('periodTo', params.periodTo);
        const qs = q.toString();
        return apiClient.get<PlatformAdvertisingDto>(
            `/seller-finance/platform-advertising${qs ? `?${qs}` : ''}`,
        );
    },

    getPlatformAdvertisingEntries: (sellerId: number) =>
        apiClient.get<PlatformAdvertisingEntryRow[]>(
            `/seller-finance/platform-advertising/${sellerId}/entries`,
        ),

    listManualAdjustments: (limit = 100) =>
        apiClient.get<SellerManualAdjustmentRow[]>(
            `/seller-finance/manual-adjustments${limit !== 100 ? `?limit=${limit}` : ''}`,
        ),

    createManualAdjustment: (body: CreateSellerManualAdjustmentPayload) =>
        apiClient.post<CreateSellerManualAdjustmentResult>(
            '/seller-finance/manual-adjustments',
            body,
        ),
};

export interface SellerManualAdjustmentRow {
    id: number;
    sellerId: number;
    shopName: string;
    slug: string;
    type: 'debit' | 'credit';
    amountRub: number;
    signedAmountRub: number;
    title: string | null;
    comment: string | null;
    adminId: number | null;
    occurredAt: string;
}

export interface CreateSellerManualAdjustmentPayload {
    sellerId: number;
    type: 'debit' | 'credit';
    amountRub: number;
    comment: string;
}

export interface CreateSellerManualAdjustmentResult {
    id: number;
    sellerId: number;
    type: 'debit' | 'credit';
    amountRub: number;
    title: string;
    occurredAt: string;
    currentBalanceRub: number;
}

export type PlatformLedgerEntryKind =
    | 'COMMISSION_INCOME'
    | 'ADVERTISING_INCOME'
    | 'SELLER_PENALTY_INCOME'
    | 'MARKETPLACE_DELIVERY_INCOME'
    | 'MARKETPLACE_DELIVERY_COST'
    | 'SELLER_DELIVERY_COMPENSATION_INCOME'
    | 'ORDER_ACCRUAL_STORNO'
    | 'DOSTAVISTA_LATE_CANCEL_COST'
    | 'MANUAL_ADJUSTMENT';

export interface PlatformLedgerEntryRow {
    id: number;
    kind: PlatformLedgerEntryKind;
    amountRub: number;
    title: string | null;
    orderId: number | null;
    orderNumber: string | null;
    sellerId: number | null;
    shopName: string | null;
    slug: string | null;
    occurredAt: string;
    meta: unknown;
}

export interface PlatformPnlLine {
    kind: PlatformLedgerEntryKind;
    label: string;
    amountRub: number;
    count: number;
}

export interface PlatformPnlDto {
    periodFrom: string | null;
    periodTo: string | null;
    lines: PlatformPnlLine[];
    net: number;
    /** Накопленно за всё время. */
    balances: {
        /** На счету: заработок платформы + сумма к выплате продавцам. */
        totalOnAccountRub: number;
        platformBalanceRub: number;
        /** Сколько ещё должны выплатить продавцам (сумма плюсовых балансов магазинов). */
        sellersPayableRub: number;
    };
}

export interface PlatformAdvertisingSellerRow {
    sellerId: number;
    shopName: string;
    slug: string;
    /** Текущий остаток кошелька (не зависит от периода). */
    balanceRub: number;
    spentRub: number;
    topUpRub: number;
    withdrawnRub: number;
    billedClicks: number;
    entriesCount: number;
    lastSpendAt: string | null;
}

export interface PlatformAdvertisingDto {
    periodFrom: string | null;
    periodTo: string | null;
    rows: PlatformAdvertisingSellerRow[];
    totals: {
        spentRub: number;
        topUpRub: number;
        withdrawnRub: number;
        balanceRub: number;
        billedClicks: number;
    };
}

export type AdvertisingLedgerEntryKind = 'TOPUP' | 'SPEND' | 'REFUND' | 'ADJUSTMENT';

export interface PlatformAdvertisingEntryRow {
    id: number;
    kind: AdvertisingLedgerEntryKind;
    amountRub: number;
    title: string | null;
    productId: number | null;
    productName: string | null;
    occurredAt: string;
}

export interface SellerAdvertisingInvoice {
    id: number;
    number: string;
    amountRub: number;
    status: 'ISSUED' | 'PAID' | 'CANCELLED';
    issuedAt: string;
    paidAt: string | null;
    cancelledAt: string | null;
}

export interface SellerAdvertisingRefund {
    id: number;
    amountRub: number;
    status: 'PENDING' | 'PAID' | 'CANCELLED';
    requestedAt: string;
    paidAt: string | null;
    cancelledAt: string | null;
}

export interface SellerAdvertisingCabinet {
    advertisingBalanceRub: number;
    /** Списания за клики за всё время. */
    totalSpentRub: number;
    walletPaused: boolean;
    invoices: SellerAdvertisingInvoice[];
    refunds: SellerAdvertisingRefund[];
}
